import { useState, useEffect } from 'react';
import { ordersAPI, appointmentsAPI, leadsAPI, ticketsAPI, crmAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, Calendar, ClipboardList, Headphones, RefreshCw, Inbox, Link2, Copy, Check, ExternalLink, Briefcase } from 'lucide-react';
import toast from 'react-hot-toast';

const TABS = [
  { key: 'orders', label: 'Orders', icon: ShoppingBag, type: 'order' },
  { key: 'appointments', label: 'Appointments', icon: Calendar, type: 'appointment' },
  { key: 'leads', label: 'Leads', icon: ClipboardList, type: 'lead' },
  { key: 'tickets', label: 'Tickets', icon: Headphones, type: 'support' },
];

const STATUS_COLORS = {
  pending: 'badge-yellow', confirmed: 'badge-blue', paid: 'badge-green', completed: 'badge-green', cancelled: 'badge-red',
  new: 'badge-blue', qualified: 'badge-green', contacted: 'badge-yellow', converted: 'badge-green', lost: 'badge-red',
  open: 'badge-yellow', in_progress: 'badge-blue', waiting: 'badge-yellow', resolved: 'badge-green', closed: 'badge-gray',
  no_show: 'badge-red',
};

export default function BookingsPage() {
  const { merchant } = useAuth();
  const [activeTab, setActiveTab] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  // Filter tabs based on merchant's business type set by super admin
  const businessType = merchant?.businessType || 'catalog';
  const visibleTabs = businessType === 'catalog'
    ? [] // catalog type has no bookings
    : TABS.filter(t => t.type === businessType);

  // Auto-select first visible tab
  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.find(t => t.key === activeTab)) {
      setActiveTab(visibleTabs[0].key);
    }
  }, [merchant, visibleTabs, activeTab]);

  useEffect(() => {
    fetchData();
  }, [activeTab, statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (statusFilter) params.status = statusFilter;

      let result;
      if (activeTab === 'orders') {
        result = await ordersAPI.list(params);
        setData(result.data.data.orders || []);
      } else if (activeTab === 'appointments') {
        result = await appointmentsAPI.list(params);
        setData(result.data.data.appointments || []);
      } else if (activeTab === 'leads') {
        result = await leadsAPI.list(params);
        setData(result.data.data.leads || []);
      } else if (activeTab === 'tickets') {
        result = await ticketsAPI.list(params);
        setData(result.data.data.tickets || []);
      }
    } catch (_) { setData([]); }
    finally { setLoading(false); }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      if (activeTab === 'orders') await ordersAPI.updateStatus(id, newStatus);
      else if (activeTab === 'appointments') await appointmentsAPI.updateStatus(id, newStatus);
      else if (activeTab === 'leads') await leadsAPI.updateStatus(id, newStatus);
      else if (activeTab === 'tickets') await ticketsAPI.updateStatus(id, newStatus);
      setData(prev => prev.map(d => d._id === id ? { ...d, status: newStatus } : d));
      toast.success('Status updated');
    } catch (_) { toast.error('Failed to update'); }
  };

  const getStatusOptions = () => {
    if (activeTab === 'orders') return ['pending', 'confirmed', 'paid', 'completed', 'cancelled'];
    if (activeTab === 'appointments') return ['pending', 'confirmed', 'completed', 'cancelled', 'no_show'];
    if (activeTab === 'leads') return ['new', 'qualified', 'contacted', 'converted', 'lost'];
    if (activeTab === 'tickets') return ['open', 'in_progress', 'waiting', 'resolved', 'closed'];
    return [];
  };

  if (visibleTabs.length === 0) {
    return (
      <div>
        <div className="page-header">
          <div><h1 className="page-title">Bookings & Data</h1><p className="page-subtitle">No booking type configured for your account</p></div>
        </div>
        <div className="page-body">
          <div className="card empty-state" style={{ padding: 60 }}>
            <Inbox size={40} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
            <h3>Not Available</h3>
            <p style={{ color: 'var(--text-muted)' }}>Your business type is set to <strong style={{ textTransform: 'capitalize' }}>{businessType}</strong>. Contact your administrator to change your business type if you need orders, appointments, leads, or support tickets.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{visibleTabs.length === 1 ? visibleTabs[0].label : 'Bookings & Data'}</h1>
          <p className="page-subtitle">
            {visibleTabs.length === 1
              ? `View ${visibleTabs[0].label.toLowerCase()} collected by AI`
              : 'View orders, appointments, leads, and support tickets collected by AI'}
          </p>
        </div>
        <button className="btn btn-secondary" onClick={fetchData}><RefreshCw size={14} /> Refresh</button>
      </div>

      <div className="page-body">
        {/* Booking Page URL */}
        {businessType === 'appointment' && merchant?.appointmentConfig?.bookingPageEnabled && (
          <BookingUrlBanner merchantId={merchant._id} />
        )}
        {/* Tabs - hidden when only one tab */}
        {visibleTabs.length > 1 && (
          <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
            {visibleTabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button key={tab.key}
                  onClick={() => { setActiveTab(tab.key); setStatusFilter(''); }}
                  style={{
                    padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                    border: 'none', background: 'none', fontSize: '0.85rem', fontWeight: 600,
                    color: activeTab === tab.key ? 'var(--primary)' : 'var(--text-muted)',
                    borderBottom: activeTab === tab.key ? '2px solid var(--primary)' : '2px solid transparent',
                    marginBottom: -1,
                  }}>
                  <Icon size={15} /> {tab.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Status Filter */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
          <button className={`btn btn-sm ${!statusFilter ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setStatusFilter('')}>All</button>
          {getStatusOptions().map(s => (
            <button key={s} className={`btn btn-sm ${statusFilter === s ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setStatusFilter(s)}>
              {s.replace('_', ' ')}
            </button>
          ))}
        </div>

        {/* Data Table */}
        {loading ? (
          <div className="flex-center" style={{ padding: 60 }}><div className="spinner" /></div>
        ) : data.length === 0 ? (
          <div className="empty-state card">
            <Inbox size={36} style={{ color: 'var(--text-muted)', marginBottom: 8 }} />
            <h3>No {activeTab} yet</h3>
            <p>When AI collects {activeTab}, they will appear here</p>
          </div>
        ) : (
          <div className="card" style={{ padding: 0, overflow: 'auto' }}>
            <table style={{ width: '100%', fontSize: '0.82rem' }}>
              <thead>
                <tr>
                  {activeTab === 'orders' && <><th>Date</th><th>Customer</th><th>Items</th><th>Total</th><th>Status</th><th>Action</th></>}
                  {activeTab === 'appointments' && <><th>Date</th><th>Time</th><th>Customer</th><th>Service</th><th>Price</th><th>Status</th><th>Action</th></>}
                  {activeTab === 'leads' && <><th>Date</th><th>Name</th><th>Phone</th><th>Email</th><th>Source</th><th>Status</th><th>Action</th></>}
                  {activeTab === 'tickets' && <><th>Ticket #</th><th>Date</th><th>Subject</th><th>Category</th><th>Priority</th><th>Status</th><th>Action</th></>}
                </tr>
              </thead>
              <tbody>
                {data.map(item => (
                  <tr key={item._id}>
                    {activeTab === 'orders' && (
                      <>
                        <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                        <td>{item.customerName || item.customerId?.name || '—'}</td>
                        <td>{item.items?.map(i => `${i.name} x${i.quantity}`).join(', ')}</td>
                        <td style={{ fontWeight: 600 }}>{item.currency} {item.total?.toFixed(2)}</td>
                        <td><span className={`badge ${STATUS_COLORS[item.status] || 'badge-gray'}`}>{item.status}</span></td>
                        <td>
                          <select className="form-input" style={{ fontSize: '0.75rem', padding: '4px 8px', width: 'auto' }}
                            value={item.status} onChange={e => updateStatus(item._id, e.target.value)}>
                            {getStatusOptions().map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                          </select>
                        </td>
                      </>
                    )}
                    {activeTab === 'appointments' && (
                      <>
                        <td>{item.date}</td>
                        <td>{item.timeSlot}</td>
                        <td>{item.customerName || item.customerId?.name || '—'}</td>
                        <td>{item.service?.name}</td>
                        <td>{item.service?.price > 0 ? `${merchant?.currency || 'USD'} ${item.service.price}` : '—'}</td>
                        <td><span className={`badge ${STATUS_COLORS[item.status] || 'badge-gray'}`}>{item.status}</span></td>
                        <td>
                          <select className="form-input" style={{ fontSize: '0.75rem', padding: '4px 8px', width: 'auto' }}
                            value={item.status} onChange={e => updateStatus(item._id, e.target.value)}>
                            {getStatusOptions().map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                          </select>
                        </td>
                      </>
                    )}
                    {activeTab === 'leads' && (
                      <>
                        <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                        <td style={{ fontWeight: 600 }}>{item.name || '—'}</td>
                        <td>{item.phone || '—'}</td>
                        <td>{item.email || '—'}</td>
                        <td>{item.source || '—'}</td>
                        <td><span className={`badge ${STATUS_COLORS[item.status] || 'badge-gray'}`}>{item.status}</span></td>
                        <td style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <select className="form-input" style={{ fontSize: '0.75rem', padding: '4px 8px', width: 'auto' }}
                            value={item.status} onChange={e => updateStatus(item._id, e.target.value)}>
                            {getStatusOptions().map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                          </select>
                          <PushToCRM lead={item} />
                        </td>
                      </>
                    )}
                    {activeTab === 'tickets' && (
                      <>
                        <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.78rem' }}>{item.ticketNumber}</td>
                        <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                        <td style={{ fontWeight: 600 }}>{item.subject}</td>
                        <td>{item.category}</td>
                        <td><span className={`badge ${item.priority === 'urgent' ? 'badge-red' : item.priority === 'high' ? 'badge-yellow' : 'badge-gray'}`}>{item.priority}</span></td>
                        <td><span className={`badge ${STATUS_COLORS[item.status] || 'badge-gray'}`}>{item.status}</span></td>
                        <td>
                          <select className="form-input" style={{ fontSize: '0.75rem', padding: '4px 8px', width: 'auto' }}
                            value={item.status} onChange={e => updateStatus(item._id, e.target.value)}>
                            {getStatusOptions().map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                          </select>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function BookingUrlBanner({ merchantId }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/book/${merchantId}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', marginBottom: 16,
      background: 'var(--primary-light)', borderRadius: 12, border: '1px solid var(--primary)',
      flexWrap: 'wrap',
    }}>
      <Link2 size={18} style={{ color: 'var(--primary)', flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--primary)' }}>Public Booking Page</div>
        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>Share this link with customers so they can book directly</div>
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <div onClick={handleCopy} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
          background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8,
          cursor: 'pointer', maxWidth: 300, overflow: 'hidden',
        }}>
          <code style={{ fontSize: '0.72rem', color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{url}</code>
          {copied ? <Check size={13} style={{ color: 'var(--green)', flexShrink: 0 }} /> : <Copy size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />}
        </div>
        <a href={url} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm">
          <ExternalLink size={13} /> Open
        </a>
      </div>
    </div>
  );
}

function PushToCRM({ lead }) {
  const navigate = useNavigate();
  const [pushed, setPushed] = useState(false);
  const [loading, setLoading] = useState(false);

  const push = async (e) => {
    e.stopPropagation();
    setLoading(true);
    try {
      await crmAPI.createDeal({
        title: lead.collectedData?.interest || lead.collectedData?.service || `Lead – ${lead.name || lead.phone}`,
        contactName: lead.name || '',
        contactEmail: lead.email || '',
        contactPhone: lead.phone || '',
        value: 0,
        source: lead.source || 'manual',
        customerId: lead.customerId,
        stage: 'new',
      });
      setPushed(true);
      toast.success('Pushed to CRM');
    } catch (_) { toast.error('Failed to push to CRM'); }
    finally { setLoading(false); }
  };

  if (pushed) return (
    <button className="btn btn-ghost btn-sm" onClick={() => navigate('/dashboard/crm')} title="View in CRM" style={{ color: 'var(--primary)' }}>
      <Briefcase size={13} />
    </button>
  );

  return (
    <button className="btn btn-ghost btn-sm" onClick={push} disabled={loading} title="Push to CRM">
      <Briefcase size={13} />
    </button>
  );
}
